import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { OnboardingWizard } from './components/onboarding';
import InstallPWA from './components/InstallPWA';
import PageLoadingFallback from './components/PageLoadingFallback';
import LazyLoadErrorBoundary from './components/LazyLoadErrorBoundary';
import { UserProvider, useUser } from './contexts/UserContext';
import { NewsProvider } from './contexts/NewsContext';
import { FoodProvider } from './contexts/FoodContext';
import { PWAProvider } from './contexts/PWAContext';

// Critical path - keep eager (users land here first)
import Home from './pages/Home';
import Pricing from './pages/Pricing';
import PlanSelection from './pages/PlanSelection';

// Lazy loaded pages - code split into separate chunks
const IOB = React.lazy(() => import('./pages/IOB'));
const DosingRules = React.lazy(() => import('./pages/DosingRules'));
const GlucoseGraph = React.lazy(() => import('./pages/GlucoseGraph'));
const Ketones = React.lazy(() => import('./pages/Ketones'));
const HbA1c = React.lazy(() => import('./pages/HbA1c'));
const ManualGlucose = React.lazy(() => import('./pages/ManualGlucose'));
const Sensors = React.lazy(() => import('./pages/Sensors'));
const Profiles = React.lazy(() => import('./pages/Profiles'));
const FoodPage = React.lazy(() => import('./pages/FoodPage'));
const MedicalReport = React.lazy(() => import('./pages/MedicalReport'));

// Settings pages - lazy loaded individually for better code splitting
const SettingsLayout = React.lazy(() => import('./pages/settings/SettingsLayout'));
const AccountSettings = React.lazy(() => import('./pages/settings/AccountSettings'));
const CgmSettings = React.lazy(() => import('./pages/settings/CgmSettings'));
const ThresholdsSettings = React.lazy(() => import('./pages/settings/ThresholdsSettings'));
const UnitsSettings = React.lazy(() => import('./pages/settings/UnitsSettings'));
const MealSettings = React.lazy(() => import('./pages/settings/MealSettings'));
const ReclaimSettings = React.lazy(() => import('./pages/settings/ReclaimSettings'));

// Help pages - lazy loaded (public access, no auth required)
const HelpLayout = React.lazy(() => import('./pages/help/HelpLayout'));
const AboutSection = React.lazy(() => import('./pages/help/AboutSection'));
const PrivacyPolicySection = React.lazy(() => import('./pages/help/PrivacyPolicySection'));
const ContactSection = React.lazy(() => import('./pages/help/ContactSection'));

// Admin pages - lazy loaded (used by <5% of users)
const Statistics = React.lazy(() => import('./pages/Statistics'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const UsersManagement = React.lazy(() => import('./pages/admin/UsersManagement'));
const FoodAdmin = React.lazy(() => import('./pages/FoodAdmin'));
const SupportedCountries = React.lazy(() => import('./pages/admin/SupportedCountries'));
const PricingManagement = React.lazy(() => import('./pages/admin/PricingManagement'));
const ReclaimManagement = React.lazy(() => import('./pages/admin/ReclaimManagement'));
const AIPromptManagement = React.lazy(() => import('./pages/admin/AIPromptManagement'));
const MealVotesManagement = React.lazy(() => import('./pages/admin/MealVotesManagement'));

// Wrapper for lazy-loaded routes with Suspense
function LazyRoute({ children }) {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      {children}
    </Suspense>
  );
}

function AppContent() {
  const { userData, loading } = useUser();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingMode, setOnboardingMode] = useState('new-user');

  useEffect(() => {
    if (userData && !loading) {
      // Profile incomplete - show onboarding wizard
      if (userData.profileComplete === false) {
        // Determine mode based on subscription status
        // If user has no subscription or is on free tier, show full wizard with subscription step
        // If user already has an active subscription, skip subscription step
        const hasActiveSubscription = userData.subscriptionStatus === 'active' ||
                                      userData.subscriptionStatus === 'trialing';
        setOnboardingMode(hasActiveSubscription ? 'profile-completion' : 'new-user');
        setShowOnboarding(true);
      }
      // Profile complete - hide onboarding
      else if (showOnboarding) {
        setShowOnboarding(false);
      }
    }
  }, [userData, loading]);

  // Block app access if onboarding is required
  if (showOnboarding && userData) {
    return (
      <>
        <OnboardingWizard open={true} mode={onboardingMode} />
      </>
    );
  }

  return (
    <>
      <InstallPWA />
      <Layout>
        <LazyLoadErrorBoundary>
          <Routes>
            {/* Critical path routes - NOT lazy loaded */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<Home />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route
              path="/graph"
              element={
                <ProtectedRoute>
                  <LazyRoute><GlucoseGraph /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plan-selection"
              element={
                <ProtectedRoute>
                  <PlanSelection />
                </ProtectedRoute>
              }
            />

            {/* Lazy loaded routes - wrapped with Suspense */}
            <Route
              path="/food-list/*"
              element={
                <ProtectedRoute role="feature-food">
                  <LazyRoute><FoodPage /></LazyRoute>
                </ProtectedRoute>
              }
            />
            {/* Legacy route - redirect to new path */}
            <Route path="/iob" element={<Navigate to="/calc/iob" replace />} />
            <Route
              path="/calc/iob"
              element={
                <ProtectedRoute>
                  <LazyRoute><IOB /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/calc/rules"
              element={
                <ProtectedRoute>
                  <LazyRoute><DosingRules /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/log/glucose"
              element={
                <ProtectedRoute>
                  <LazyRoute><ManualGlucose /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/log/ketones"
              element={
                <ProtectedRoute>
                  <LazyRoute><Ketones /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/log/hba1c"
              element={
                <ProtectedRoute>
                  <LazyRoute><HbA1c /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sensors"
              element={
                <ProtectedRoute>
                  <LazyRoute><Sensors /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profiles"
              element={
                <ProtectedRoute>
                  <LazyRoute><Profiles /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/medical"
              element={
                <ProtectedRoute>
                  <LazyRoute><MedicalReport /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <LazyRoute><SettingsLayout /></LazyRoute>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/settings/account" replace />} />
              <Route path="account" element={<LazyRoute><AccountSettings /></LazyRoute>} />
              <Route path="cgm" element={<LazyRoute><CgmSettings /></LazyRoute>} />
              <Route path="thresholds" element={<LazyRoute><ThresholdsSettings /></LazyRoute>} />
              <Route path="units" element={<LazyRoute><UnitsSettings /></LazyRoute>} />
              <Route path="meal" element={<LazyRoute><MealSettings /></LazyRoute>} />
              <Route path="reclaim" element={<LazyRoute><ReclaimSettings /></LazyRoute>} />
            </Route>

            {/* Help routes - PUBLIC (no auth required) */}
            <Route path="/help" element={<LazyRoute><HelpLayout /></LazyRoute>}>
              <Route index element={<Navigate to="/help/about" replace />} />
              <Route path="about" element={<LazyRoute><AboutSection /></LazyRoute>} />
              <Route path="privacy" element={<LazyRoute><PrivacyPolicySection /></LazyRoute>} />
              <Route path="contact" element={<LazyRoute><ContactSection /></LazyRoute>} />
            </Route>

            {/* Admin routes - lazy loaded */}
            <Route path="/statistics" element={<Navigate to="/admin/statistics" replace />} />
            <Route
              path="/admin/statistics"
              element={
                <ProtectedRoute role="statistics-admin">
                  <LazyRoute><Statistics /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute role="dashboard-admin">
                  <LazyRoute><AdminDashboard /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute role="users-admin">
                  <LazyRoute><UsersManagement /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/food"
              element={
                <ProtectedRoute role="food-admin">
                  <LazyRoute><FoodAdmin /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/countries"
              element={
                <ProtectedRoute role="dashboard-admin">
                  <LazyRoute><SupportedCountries /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pricing"
              element={
                <ProtectedRoute role="price-admin">
                  <LazyRoute><PricingManagement /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reclaim"
              element={
                <ProtectedRoute role="sensor-reclaim-admin">
                  <LazyRoute><ReclaimManagement /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/ai-prompts"
              element={
                <ProtectedRoute role="food-admin">
                  <LazyRoute><AIPromptManagement /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/meal-votes"
              element={
                <ProtectedRoute role="food-admin">
                  <LazyRoute><MealVotesManagement /></LazyRoute>
                </ProtectedRoute>
              }
            />
          </Routes>
        </LazyLoadErrorBoundary>
      </Layout>
    </>
  );
}

function App() {
  return (
    <PWAProvider>
      <UserProvider>
        <NewsProvider>
          <FoodProvider>
            <AppContent />
          </FoodProvider>
        </NewsProvider>
      </UserProvider>
    </PWAProvider>
  );
}

export default App;
